import { Text } from "@solomon-labs/design/components/ui";
import type React from "react";

import { cn } from "../../utils/cn";

/**
 * `Heading` — universal title-pair element. Pairs an optional eyebrow
 * label, a required title, and an optional subtitle in a consistent
 * vertical stack. Optional bottom-border divider for section-header
 * use cases.
 *
 * Lives in `ui/` because it bakes no typography opinions — every
 * variant (eyebrow, title, subtitle) is exposed as a prop, and the
 * default values cover the most common case (h1 title, small
 * subtitle, label eyebrow).
 *
 * Replaces inline `<div className="flex flex-col gap-1"><Text label
 * /><Text h?/><Text small /></div>` clusters across:
 * - KPI tiles (eyebrow + title)
 * - Page headers with eyebrows (eyebrow + title)
 * - Chart-frame headers (title + subtitle)
 * - Section / panel step headers (eyebrow + title or title + subtitle)
 *
 * The two visual layouts ("eyebrow above + title below" and "title
 * above + subtitle below") are not separate components — they're
 * achieved by which props the consumer passes:
 *   - eyebrow + title         → eyebrow-above pattern
 *   - title + subtitle        → title-above pattern
 *   - eyebrow + title + sub   → full stack (Welcome eyebrow + h1 + small description)
 *
 * @see ARCHITECTURE.md principle #2 — recipe-by-prop, not component
 *      proliferation.
 */
export interface HeadingProps {
    /**
     * Optional small label rendered above the title (the "eyebrow"
     * pattern). Conventionally muted-foreground. Always rendered as
     * `<Text variant="label">`.
     */
    eyebrow?: React.ReactNode;

    /** Required title content. Rendered with `titleVariant`. */
    title: React.ReactNode;

    /**
     * Typography for the title. Default `h1` — main app's hero
     * positions can adopt freely without typography conflicts.
     * Console consumers typically pick `h2` or `h3` for nested
     * sections (page-h1 stays at the top, sub-section headers step
     * down).
     */
    titleVariant?: "hero" | "h1" | "h2" | "h3" | "body";

    /**
     * Optional copy below the title. Conventionally muted-foreground.
     */
    subtitle?: React.ReactNode;

    /**
     * Typography for the subtitle. Default `small`. Pick `body` for
     * descriptive prose, `label` for tight metadata.
     */
    subtitleVariant?: "small" | "body" | "label" | "fine-print";

    /**
     * Optional right-side content rendered beside the title-pair
     * stack — typically a Button or a cluster of Buttons. When set,
     * the wrapper switches from a vertical stack to a flex row with
     * the title-pair on the left (baseline-aligned) and the actions
     * on the right.
     *
     * Used for page-level chrome (PageHeader recipe — see intent file)
     * and section headers with affordances (the "Add" / "Edit" /
     * "View all" pattern).
     */
    actions?: React.ReactNode;

    /**
     * Adds `pb-4 border-b border-line-base` so the heading reads as
     * a section divider. Default `false`. Compose with className for
     * custom padding/border tones.
     */
    divider?: boolean;

    /**
     * Semantic element for the wrapper. Default `"div"`. Use
     * `"header"` at the top of a page or panel; `"section"` for
     * standalone block headers.
     */
    asElement?: "div" | "header" | "section";

    /**
     * Title-color tone. Semantic enum aligned to the design system's
     * foreground + status tokens. Eyebrow + subtitle stay
     * `text-foreground-muted` regardless — `tone` only affects the
     * title text node.
     *
     *   base    (default) → text-foreground-base
     *   muted   → text-foreground-muted     (secondary heading)
     *   imprint → text-foreground-imprint   (very subdued — for "inactive" semantics)
     *   error   → text-error-base           (problem / precondition warnings)
     *   warning → text-warning-base         (attention)
     *   success → text-success-base         (positive state)
     *
     * Bold weight is governed by `titleVariant`, not `tone` — `h2` is
     * the only bold variant in the type system; pick `titleVariant="h2"`
     * if you want bold typography on the heading.
     */
    tone?: HeadingTone;

    /**
     * Extra utilities merged onto the **title `Text` node** (not the
     * wrapper — `className` handles that). For the rare case where the
     * title needs a size or tracking override the `titleVariant` scale
     * can't express — e.g. an `h2`-weighted title bumped to `text-3xl`.
     * Merged after the tone class, so a size utility here wins over the
     * variant's own size (Tailwind source order). Keep it to overrides
     * the variant genuinely can't cover, not per-instance restyling.
     */
    titleClassName?: string;

    className?: string;
}

export type HeadingTone =
    | "base"
    | "muted"
    | "imprint"
    | "error"
    | "warning"
    | "success";

const TITLE_TONE: Record<HeadingTone, string> = {
    base: "text-foreground-base",
    muted: "text-foreground-muted",
    imprint: "text-foreground-imprint",
    error: "text-error-base",
    warning: "text-warning-base",
    success: "text-success-base"
};

const ANCILLARY_FOREGROUND = "text-foreground-muted";

export function Heading({
    eyebrow,
    title,
    titleVariant = "h1",
    subtitle,
    subtitleVariant = "small",
    actions,
    divider = false,
    asElement = "div",
    tone = "base",
    titleClassName,
    className
}: HeadingProps): React.ReactElement {
    const Tag = asElement as React.ElementType;
    const hasActions = actions !== undefined && actions !== null;

    const titleStack = (
        <>
            {eyebrow !== undefined && eyebrow !== null && (
                <Text variant="label" className={ANCILLARY_FOREGROUND}>
                    {eyebrow}
                </Text>
            )}
            <Text
                variant={titleVariant}
                className={cn(TITLE_TONE[tone], titleClassName)}
            >
                {title}
            </Text>
            {subtitle !== undefined && subtitle !== null && (
                <Text
                    variant={subtitleVariant}
                    className={ANCILLARY_FOREGROUND}
                >
                    {subtitle}
                </Text>
            )}
        </>
    );

    if (hasActions) {
        return (
            <Tag
                className={cn(
                    "flex items-baseline justify-between gap-4",
                    divider && "pb-4 border-b border-line-base",
                    className
                )}
            >
                <div className="flex flex-col gap-1 min-w-0">{titleStack}</div>
                <div className="shrink-0">{actions}</div>
            </Tag>
        );
    }

    return (
        <Tag
            className={cn(
                "flex flex-col gap-1",
                divider && "pb-4 border-b border-line-base",
                className
            )}
        >
            {titleStack}
        </Tag>
    );
}
